import React, { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AllocationBar from '@/components/organisms/AllocationBar';
import { accountingCenterService } from '@/services/accounting-centers.service';
import InputField from '@/components/atoms/InputField';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';

const ACAdjustPage = () => {
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const location = useLocation();
  // const acCenter = location.state?.acCenter as any | undefined;
  const [accountingCenters, setAccountingCenters] = useState<any[]>([]);
  const [editedValues, setEditedValues] = useState<{ [id: string]: number }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [acCenter, setAcCenter] = useState<any | undefined>(undefined);
  const [totalBudget, setTotalBudget] = useState({ acmev: 0, acrt: 0, acmev_used: 0 });

  const { budgetId, acType } = useParams();

  const [editViewMode, setEditViewMode] = useState('acmev');

  // Calculate maximum allowed total based on current tab
  const maxAllowedTotal = editViewMode === 'acmev' ? totalBudget.acmev : totalBudget.acrt;

  const navigate = useNavigate();

  // Calculate chart data for ACMEV
  const acmevTotal = totalBudget.acmev;
  const acmevUsed = totalBudget.acmev_used;
  const acmevRemaining = Math.max(0, acmevTotal - acmevUsed);

  const acmevChartData = [
    {
      key: 'remaining',
      value: acmevTotal === 0 ? 0 : Math.round((acmevRemaining / acmevTotal) * 100),
      fill: '#32ADE6'
    },
    {
      key: 'spent',
      value: acmevTotal === 0 ? 0 : Math.round((acmevUsed / acmevTotal) * 100),
      fill: '#FF2D55'
    }
  ];

  const acmevChartConfig = {
    remaining: { label: 'Remaining Budget', color: '#32ADE6' },
    spent: { label: 'Budget Spent', color: '#FF2D55' }
  };

  const acColors = [
    '#7663EB', // First
    '#48C80C', // Second
    '#007AFF', // Third
    '#00C7BE', // Fourth
    '#A2845E', // Fifth
    '#FF9500', // Sixth
    '#FF2D55', // Seventh
    '#FFCC00', // Eighth
    '#94B665', // Ninth
    '#774E19' // Tenth
  ];

  const fetchAccountCenterDetails = async () => {
    // Fetch accounting center details
    try {
      const { data: accountingCentersData } = await accountingCenterService.getAccountingCentersByType(
        budgetId || '',
        String(acType).toUpperCase() || ''
      );

      // Calculate totals for all accounting centers
      const totals = accountingCentersData.reduce(
        (acc, ac) => ({
          acmev: acc.acmev + (ac.acmev || 0),
          acrt: acc.acrt + (ac.acrt || 0),
          acmev_used: acc.acmev_used + (ac.acmev_used || 0)
        }),
        { acmev: 0, acrt: 0, acmev_used: 0 }
      );

      setTotalBudget(totals);
      setAcCenter(accountingCentersData[0]); // Keep for any other use cases

      const formattedAccountingCenters = accountingCentersData.map((ac: any, idx: number) => ({
        name: ac.name,
        _id: ac._id ?? `ac-${idx}`,
        acmev: ac.acmev,
        acrt: ac.acrt,
        color: acColors[idx % acColors.length] || '#ccc'
      }));

      setAccountingCenters(formattedAccountingCenters);
    } catch (error) {
      console.error('Error fetching accounting center details:', error);
    }
  };

  useEffect(() => {
    fetchAccountCenterDetails();
  }, [budgetId, acType]);

  // Only initialize editedValues once when accountingCenters are loaded
  useEffect(() => {
    if (accountingCenters.length > 0 && !isInitialized) {
      const initialValues: { [id: string]: number } = {};
      accountingCenters.forEach((ac: any) => {
        initialValues[ac._id] = editViewMode === 'acmev' ? ac.acmev : ac.acrt;
      });
      setEditedValues(initialValues);
      setIsInitialized(true);
    }
  }, [accountingCenters, editViewMode, isInitialized]);

  // Update editedValues when switching between ACMEV and ACRT tabs
  useEffect(() => {
    if (accountingCenters.length > 0 && isInitialized) {
      const updatedValues: { [id: string]: number } = {};
      accountingCenters.forEach((ac: any) => {
        updatedValues[ac._id] = editViewMode === 'acmev' ? ac.acmev : ac.acrt;
      });
      setEditedValues(updatedValues);
    }
  }, [editViewMode, accountingCenters, isInitialized]);

  const handleSave = async () => {
    // Determine the key based on the selected tab
    const key = editViewMode; // 'acmev' or 'acrt'
    // Map editedValues to the desired array format
    const payload = Object.entries(editedValues).map(([id, value]) => ({
      id,
      [key]: value
    }));

    try {
      if (editViewMode === 'acmev') {
        await accountingCenterService.adjustAcmevAllocation(budgetId || '', { acs: payload });
      } else {
        await accountingCenterService.adjustAcrtAllocation(budgetId || '', { acs: payload });
      }
      toast.success('Allocation updated successfully');

      // Refresh the accounting centers data to get updated values
      await fetchAccountCenterDetails();
    } catch (error) {
      console.error('Error adjusting allocation:', error);
    }
  };

  useEffect(() => {
    const total = Object.values(editedValues).reduce((acc, val) => acc + (Number(val) || 0), 0);
    setTotalAmount(total);
  }, [editedValues]);

  return (
    <>
      <MobileAppBar title={<span className="capitalize">Edit {acType?.split('_')[0]} ACs Allocation</span>} />

      <div className="flex flex-col p-4 md:p-0 md:flex-row gap-4 md:gap-6 flex-1">
        {/* Tabs ACMEV/ACRT (MOBILE) */}
        <Tabs defaultValue="acmev" value={editViewMode} onValueChange={setEditViewMode} className="md:hidden">
          <TabsList className="bg-lightPurple-200 flex gap-4 h-auto py-[0.2rem]">
            <TabsTrigger value="acmev" className="flex-1 w-1/2 group md:py-[0.3rem]">
              <span>ACMEV</span>
            </TabsTrigger>
            <TabsTrigger value="acrt" className="flex-1 w-1/2 group md:py-[0.3rem]">
              <span>ACRT</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="h-fit flex items-center justify-between gap-4 md:border bg-white rounded-[16px] p-4 py-8">
          <div className="flex flex-col gap-2">
            <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
              <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                  <img
                    src={percentageIcon}
                    alt="percentage-icon"
                    width={13}
                    height={17}
                    className="group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
                <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                  <img
                    src={euroIcon}
                    alt="euro-icon"
                    width={12}
                    height={12}
                    className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                  />
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <p className="text-sm text-secondary">Remaining {editViewMode.toUpperCase()}</p>
            <p className="text-[28px] font-bold leading-[24px] mb-2 md:mb-0">
              {graphViewMode === 'percentage' ? `${acmevChartData[0].value}%` : `€${acmevRemaining.toLocaleString()}`}
            </p>
            <div>
              <div className="flex items-center gap-2">
                <span className="w-[9px] h-[9px] rounded-full bg-[#32ADE6]"></span>
                <p className="text-sm text-secondary">Remaining Budget</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-[9px] h-[9px] rounded-full bg-[#FF2D55]"></span>
                <p className="text-sm text-secondary">Budget Spent</p>
              </div>
            </div>
          </div>
          <div>
            <PieGraph
              title="Remaining BMEV"
              titleValue={
                graphViewMode === 'percentage' ? `${acmevChartData[0].value}%` : `€${acmevRemaining.toLocaleString()}`
              }
              chartConfig={acmevChartConfig}
              chartData={acmevChartData}
              className=""
              outerRadius={64}
              minHeight={140}
              hideLabel={true}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-4">
          <div className="hidden md:flex border-b items-center gap-4">
            <button
              className={`text-lg py-2 text-secondary border-b-2 ${editViewMode === 'acmev' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setEditViewMode('acmev')}
            >
              ACMEV
            </button>
            <button
              className={`text-lg py-2 text-secondary border-b-2 ${editViewMode === 'acrt' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setEditViewMode('acrt')}
            >
              ACRT
            </button>
          </div>

          {/* Allocation section title (MOBILE) */}
          <h4 className="text-[20px] font-bold mb-0 py-2 md:hidden">Allocation</h4>

          <div className="md:border bg-white rounded-[16px] p-4 flex-1 flex flex-col">
            <h4 className="text-[20px] font-bold mb-4 hidden md:block">Allocation</h4>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{editViewMode.toUpperCase()} Allocation</p>
                <span className="text-primary text-xs font-bold">?</span>
              </div>
              <div className="text-sm text-secondary">
                <span className={`font-medium ${totalAmount !== maxAllowedTotal ? 'text-red-500' : 'text-green-600'}`}>
                  €{totalAmount.toLocaleString()}
                </span>
                <span className="mx-1">/</span>
                <span className="font-medium">€{maxAllowedTotal.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex-1 mt-4">
              <div className={`${totalAmount !== maxAllowedTotal ? 'border border-[#E8507E] rounded-xl' : ''}`}>
                <AllocationBar
                  items={accountingCenters.map((ac) => ({
                    value: Number(editedValues[ac._id]) || 0,
                    label: ac.name,
                    color: ac.color
                  }))}
                  maxAllowedTotal={maxAllowedTotal}
                  height={22}
                  borderRadius={12}
                />
              </div>
              <div className="flex flex-col gap-4 mt-4">
                {accountingCenters.map((ac) => (
                  <div className="flex items-center justify-between" key={ac._id}>
                    <div className="flex-1 flex items-center gap-3">
                      <span className="w-[9px] h-[9px] rounded-full" style={{ backgroundColor: ac.color }}></span>
                      <p className="text-sm font-medium">{ac.name}</p>
                    </div>
                    <InputField
                      name={editViewMode}
                      label=""
                      placeholder="10000"
                      type="text"
                      icon={euroIcon}
                      iconAlt="Euro"
                      className="w-[136px]"
                      size="sm"
                      value={
                        editedValues[ac._id] !== undefined && editedValues[ac._id] !== null
                          ? String(editedValues[ac._id])
                          : ''
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.replace(/[^\d.]/g, '');
                        const numericValue = value === '' ? 0 : Number(value);

                        // Calculate what the new total would be with this new value
                        const newTotal = Object.entries(editedValues).reduce((acc, [id, val]) => {
                          if (id === ac._id) {
                            return acc + numericValue;
                          }
                          return acc + (Number(val) || 0);
                        }, 0);

                        // If new total is within limit, allow the change
                        if (newTotal <= maxAllowedTotal) {
                          setEditedValues((prev) => ({ ...prev, [ac._id]: value === '' ? '' : numericValue }));
                        } else {
                          // Show warning toast and keep the previous value (don't update)
                          toast.error(`Total allocation cannot exceed €${maxAllowedTotal.toLocaleString()}`);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Error message when total doesn't equal maximum */}
              {totalAmount !== maxAllowedTotal && (
                <p className="text-[#E8507E] text-sm font-medium mt-4">
                  The Allocation must have a value equal to the {editViewMode.toUpperCase()} (€
                  {maxAllowedTotal.toLocaleString()})
                </p>
              )}
            </div>

            <div className="flex justify-end mt-4 gap-4">
              <button onClick={() => navigate(-1)} className="hidden md:block text-[#E8507E] text-sm font-semibold">
                Back
              </button>
              <Button
                className="w-full md:w-auto text-base md:text-sm uppercase md:capitalize bg-primary text-white rounded-full px-6"
                onClick={handleSave}
                disabled={totalAmount !== maxAllowedTotal}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ACAdjustPage;
